import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { buildTableLabels } from '@/config/tableLabels';
import { customerFilter, materialFilter } from '@/config/filterDefinitions';
import {
  useTableState,
  useTranslations,
  useGenuinelyEmpty,
  UnitTypeContext,
  useUnitTypeHeader,
} from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetTransactionsQuery,
  useGetTransactionFilterOptionsQuery,
} from '../api/transactionsApi';
import { TransactionAttachmentsModal } from './TransactionAttachmentsModal';
import { buildTransactionsColumns } from '../constants/transactionsColumns';
import { TRANSACTION_FILTERS } from '../constants/transactions.constants';
import { buildUnitHeaderOptions } from '../constants/transactionUnits';

const getTransactionRowId = (row) => row.id;
// An outgoing declaration's row IS its allocation (same id), whose page holds the declaration.
// TODO: incoming declarations once `Transaction` carries its declaration's id, not just `sdNo`.
const VIEWABLE_DECLARATION_TYPES = Object.freeze(['OUTGOING_DECLARATION']);
const cannotViewDeclaration = (row) => !VIEWABLE_DECLARATION_TYPES.includes(row.typeCode);
// Table sizes this from both action labels; the design gives the column 262px.
const ACTIONS_WIDTH = 262;

/**
 * Props:
 *   module           the active module route segment, for the attachments read and links
 *   certificateType  the module's `CertificateType`, which narrows the ledger
 *   className        forwarded to <Table>
 *   extraFilters            filters merged on top of the panel's, e.g. the inventory drill-down's
 *   emptyState       <Table>'s emptyState
 *   noResults        <Table>'s noResults
 *   onEmptyChange    (isEmpty) => void — true only when nothing matches and nothing is filtered
 */
export const TransactionsTable = ({
  module,
  certificateType,
  className,
  extraFilters,
  emptyState,
  noResults,
  onEmptyChange,
}) => {
  const t = useTranslations();
  const table = useTableState();

  const queryArgs = useMemo(
    () => ({ certificateType, ...table.queryParams, extraFilters }),
    [certificateType, table.queryParams, extraFilters],
  );
  const { data, isFetching, isError } = useGetTransactionsQuery(queryArgs);
  const rows = data?.rows ?? EMPTY_LIST;
  const total = data?.totalCount ?? 0;

  useGenuinelyEmpty({ table, isFetching, isError, total }, onEmptyChange);

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  const navigate = useNavigate();
  const handleViewDeclaration = useCallback(
    (row) => navigate(ROUTES.allocationDetail(module, row.id)),
    [navigate, module],
  );

  const [attachmentsRow, setAttachmentsRow] = useState(null);
  const closeAttachments = useCallback(() => setAttachmentsRow(null), []);

  const rowActions = useMemo(
    () => [
      {
        id: 'view-declaration',
        label: t('TRANSACTIONS_ACTION_VIEW_DECLARATION'),
        disabled: cannotViewDeclaration,
        onClick: handleViewDeclaration,
      },
      {
        id: 'view-attachments',
        label: t('TRANSACTIONS_ACTION_VIEW_ATTACHMENTS'),
        hidden: (row) => !row.hasAttachments,
        onClick: setAttachmentsRow,
      },
    ],
    [t, handleViewDeclaration],
  );

  const columns = useMemo(
    () => buildTransactionsColumns(t, { unitHeaderSelect }),
    [t, unitHeaderSelect],
  );

  const filterOptionsArgs = useMemo(
    () => ({ certificateType, extraFilters }),
    [certificateType, extraFilters],
  );
  const { data: filterOptions } = useGetTransactionFilterOptionsQuery(filterOptionsArgs);

  const filterDefinitions = useMemo(
    () => [
      ...materialFilter(
        t,
        TRANSACTION_FILTERS.materialId,
        filterOptions?.materialOptions ?? EMPTY_LIST,
      ),
      ...customerFilter(
        t,
        TRANSACTION_FILTERS.counterpartyAccountId,
        filterOptions?.counterpartyOptions ?? EMPTY_LIST,
      ),
    ],
    [t, filterOptions],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  return (
    <>
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          className={className}
          columns={columns}
          data={rows}
          total={total}
          loading={isFetching}
          getRowId={getTransactionRowId}
          searchable
          onSearchChange={handleSearchChange}
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          rowActions={rowActions}
          actionsHeader={t('FIELD_ACTION')}
          actionsWidth={ACTIONS_WIDTH}
          emptyState={emptyState}
          noResults={noResults}
          labels={tableLabels}
        />
      </UnitTypeContext.Provider>

      <TransactionAttachmentsModal
        open={attachmentsRow != null}
        onClose={closeAttachments}
        module={module}
        transaction={attachmentsRow}
      />
    </>
  );
};
